/**
 * AI Configuration Panel
 * Manage Groq API key, Ollama URL, and service preferences
 */

import React, { useState } from 'react'
import { Settings, Eye, EyeOff, Save, AlertCircle, CheckCircle } from 'lucide-react'
import aiService from '@/services/aiService'

export default function AIConfigPanel({ config, onConfigUpdate }) {
  const [formData, setFormData] = useState({
    groqApiKey: '',
    ollamaUrl: config?.ollamaUrl || 'http://localhost:11434',
    defaultModel: config?.defaultModel || 'hybrid',
    groqEnabled: config?.groqEnabled !== false,
    ollamaEnabled: config?.ollamaEnabled !== false,
  })

  const [showApiKey, setShowApiKey] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setSaved(false)
    setError(null)
  }

  const handleSave = () => {
    try {
      // Validate inputs
      if (formData.groqEnabled && !formData.groqApiKey) {
        setError('Groq API key required when Groq is enabled')
        return
      }

      if (formData.ollamaEnabled && !formData.ollamaUrl) {
        setError('Ollama URL required when Ollama is enabled')
        return
      }

      // Configure services
      const configUpdate = {
        groqApiKey: formData.groqApiKey || undefined,
        ollamaUrl: formData.ollamaUrl,
        defaultModel: formData.defaultModel,
        groqEnabled: formData.groqEnabled,
        ollamaEnabled: formData.ollamaEnabled,
      }

      aiService.configureServices(configUpdate)
      setSaved(true)
      setError(null)

      // Trigger parent refresh
      setTimeout(() => {
        onConfigUpdate()
      }, 500)
    } catch (err) {
      setError('Failed to save configuration: ' + err.message)
    }
  }

  return (
    <div className="space-y-6">
      {/* Configuration Form */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Settings className="w-5 h-5 text-blue-400" />
          Service Configuration
        </h2>

        <div className="space-y-6">
          {/* Groq Configuration */}
          <div className="border border-slate-600 rounded-lg p-6 bg-slate-700/20">
            <h3 className="text-lg font-semibold text-blue-300 mb-4">Groq Cloud Configuration</h3>

            <div className="space-y-4">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-white font-medium">Enable Groq</label>
                  <p className="text-sm text-slate-400 mt-1">Use Groq for analysis, insights, and recommendations</p>
                </div>
                <button
                  onClick={() => handleInputChange('groqEnabled', !formData.groqEnabled)}
                  className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                    formData.groqEnabled ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                      formData.groqEnabled ? 'translate-x-9' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* API Key Input */}
              <div>
                <label className="text-white font-medium block mb-2">Groq API Key</label>
                <div className="flex items-center gap-2">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={formData.groqApiKey}
                    onChange={(e) => handleInputChange('groqApiKey', e.target.value)}
                    placeholder="gsk_..."
                    disabled={!formData.groqEnabled}
                    className="flex-1 bg-slate-700 border border-slate-600 rounded px-4 py-2 text-white placeholder-slate-500 disabled:opacity-50 focus:outline-none focus:border-blue-400"
                  />
                  <button
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="text-slate-400 hover:text-slate-300 p-2"
                  >
                    {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Get free key from console.groq.com</p>
              </div>
            </div>
          </div>

          {/* Ollama Configuration */}
          <div className="border border-slate-600 rounded-lg p-6 bg-slate-700/20">
            <h3 className="text-lg font-semibold text-purple-300 mb-4">Ollama Local Configuration</h3>

            <div className="space-y-4">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-white font-medium">Enable Ollama</label>
                  <p className="text-sm text-slate-400 mt-1">Use Ollama for voice, persona, and learning</p>
                </div>
                <button
                  onClick={() => handleInputChange('ollamaEnabled', !formData.ollamaEnabled)}
                  className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                    formData.ollamaEnabled ? 'bg-purple-600' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                      formData.ollamaEnabled ? 'translate-x-9' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* URL Input */}
              <div>
                <label className="text-white font-medium block mb-2">Ollama URL</label>
                <input
                  type="text"
                  value={formData.ollamaUrl}
                  onChange={(e) => handleInputChange('ollamaUrl', e.target.value)}
                  placeholder="http://localhost:11434"
                  disabled={!formData.ollamaEnabled}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-4 py-2 text-white placeholder-slate-500 disabled:opacity-50 focus:outline-none focus:border-purple-400"
                />
                <p className="text-xs text-slate-500 mt-2">Default: http://localhost:11434</p>
              </div>
            </div>
          </div>

          {/* Default Model Selection */}
          <div className="border border-slate-600 rounded-lg p-6 bg-slate-700/20">
            <h3 className="text-lg font-semibold text-cyan-300 mb-4">Task Routing</h3>

            <div>
              <label className="text-white font-medium block mb-3">Default Model</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  {
                    value: 'hybrid',
                    label: 'Hybrid (Recommended)',
                    desc: 'Routes tasks intelligently between Groq and Ollama',
                  },
                  {
                    value: 'groq',
                    label: 'Groq Only',
                    desc: 'Use only cloud AI for all tasks',
                  },
                  {
                    value: 'ollama',
                    label: 'Ollama Only',
                    desc: 'Use only local model for all tasks',
                  },
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleInputChange('defaultModel', option.value)}
                    className={`border-2 rounded-lg p-3 text-left transition-all ${
                      formData.defaultModel === option.value
                        ? 'border-blue-400 bg-blue-500/10'
                        : 'border-slate-600 hover:border-slate-500 bg-slate-700/20'
                    }`}
                  >
                    <p className="font-medium text-white">{option.label}</p>
                    <p className="text-xs text-slate-400 mt-1">{option.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {saved && (
          <div className="mt-6 bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <p className="text-emerald-300 text-sm">Configuration saved successfully!</p>
          </div>
        )}

        {/* Save Button */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <Save className="w-4 h-4" />
            Save Configuration
          </button>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-orange-900/20 rounded-xl border border-orange-500/30 p-6">
        <h3 className="text-lg font-semibold text-orange-300 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Security Note
        </h3>
        <div className="text-sm text-orange-100 space-y-2">
          <p>✓ <strong>Never commit API keys</strong> to version control</p>
          <p>✓ <strong>Use environment variables</strong> for sensitive data</p>
          <p>✓ <strong>Backend proxy recommended</strong> for Groq API calls (see AI_SETUP_GUIDE.md)</p>
          <p>✓ <strong>Keep Ollama local</strong> on trusted machines only</p>
        </div>
      </div>
    </div>
  )
}
